import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../services/api/apiService";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import { eid } from "../../utils/entityId";
import "../../styles/features/matches.css";

/**
 * PendingChallenges
 * Shows match challenges that are waiting for the current user's team to accept / decline.
 * Embedded inside MatchList or used standalone.
 */
const PendingChallenges = ({ onChallengeResolved }) => {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null); // matchId being responded to

  const loadChallenges = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/matches/my-challenges");
      setChallenges(res.data?.data || []);
    } catch {
      setChallenges([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChallenges();
  }, [loadChallenges]);

  const respond = async (matchId, action) => {
    setResponding(matchId);
    try {
      await apiClient.post(`/matches/${matchId}/respond-challenge`, { action });
      await loadChallenges();
      if (onChallengeResolved) onChallengeResolved();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to respond to challenge");
    } finally {
      setResponding(null);
    }
  };

  const formatDate = (dt) => {
    if (!dt) return "TBD";
    return new Date(dt).toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) return null; // Silent while loading to avoid layout shift
  if (challenges.length === 0) return null;

  // Separate incoming (can_respond) from outgoing (waiting)
  const incoming = challenges.filter((c) => c.can_respond);
  const outgoing = challenges.filter((c) => !c.can_respond);

  return (
    <div className="pending-challenges-section">
      <FloatingCard variant="primary" size="large" className="delay-1">
        <div className="challenges-header">
          <h3 className="form-section-title">
            ⚔️ Pending Match Challenges
            <span className="challenge-count-badge">{challenges.length}</span>
          </h3>
          <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginTop: "0.25rem" }}>
            {incoming.length > 0
              ? `${incoming.length} incoming challenge${incoming.length > 1 ? "s" : ""} waiting for your response`
              : "Challenges sent — waiting for opponents to respond"}
          </p>
        </div>

        <div className="challenges-list">
          {challenges.map((c) => {
            const challengerName = c.challenger_team_name || c.team1_name || "Team A";
            const challengedName = c.challenged_team_name || c.team2_name  || "Team B";
            return (
              <div key={c.id} className={`challenge-item ${c.can_respond ? "challenge-item--incoming" : "challenge-item--outgoing"}`}>
                {/* Role banner */}
                <div className={`challenge-role-banner ${c.can_respond ? "banner-incoming" : "banner-outgoing"}`}>
                  {c.can_respond
                    ? `🔔 ${challengerName} is challenging your team — respond below`
                    : `📤 You challenged ${challengedName} — waiting for their response`}
                </div>

                <div className="challenge-teams">
                  <span className="challenge-team challenger">{challengerName}</span>
                  <span className="challenge-vs">⚔️</span>
                  <span className="challenge-team challenged">{challengedName}</span>
                </div>

                <div className="challenge-meta">
                  {c.sport_name && (
                    <span className="challenge-sport">{c.sport_name}</span>
                  )}
                  {c.scheduled_at && (
                    <span className="challenge-date">📅 {formatDate(c.scheduled_at)}</span>
                  )}
                  {c.venue && (
                    <span className="challenge-venue">📍 {c.venue}</span>
                  )}
                </div>

                <div className="challenge-actions">
                  {c.can_respond ? (
                    <>
                      <FloatingButton
                        variant="success"
                        size="small"
                        icon="✅"
                        disabled={responding === c.id}
                        onClick={() => respond(c.id, "accept")}
                      >
                        <span className="btn-text">
                          {responding === c.id ? "Accepting…" : "Accept Challenge"}
                        </span>
                      </FloatingButton>
                      <FloatingButton
                        variant="danger"
                        size="small"
                        icon="❌"
                        disabled={responding === c.id}
                        onClick={() => respond(c.id, "decline")}
                      >
                        <span className="btn-text">
                          {responding === c.id ? "Declining…" : "Decline"}
                        </span>
                      </FloatingButton>
                    </>
                  ) : (
                    <span className="challenge-waiting-label">
                      ⏳ Awaiting {challengedName}'s response
                    </span>
                  )}
                  <FloatingButton
                    variant="outline"
                    size="small"
                    onClick={() => navigate(`/matches/${eid(c)}`)}
                  >
                    <span className="btn-text">View</span>
                  </FloatingButton>
                </div>
              </div>
            );
          })}
        </div>
      </FloatingCard>
    </div>
  );
};

export default PendingChallenges;
